import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Cart } from '../models/cart.model';
import { Product } from '../models/product.model';
import { StorefrontOrder } from '../models/order.model';
import { getProductUnitPrice } from './meta-pixel.helpers';
import { resolveMerchantId } from './merchant-id.util';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const GA4_CURRENCY = 'EGP';

@Injectable({ providedIn: 'root' })
export class GtagService {
  /** GA4 is owned by GTM (`GTM-P6Q759RG`). Do not load gtag.js here. */
  private readonly purchaseDedupePrefix = 'zt.ga4.purchase.';

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {}

  get isEnabled(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  init(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.dataLayer = window.dataLayer || [];
  }

  trackPageView(url: string, title?: string): void {
    this.push({
      event: 'page_view',
      page_path: url,
      page_title: title ?? (typeof document !== 'undefined' ? document.title : ''),
    });
  }

  trackViewItem(product: Product): void {
    const price = getProductUnitPrice(product);
    this.ecommerce('view_item', {
      currency: GA4_CURRENCY,
      value: price,
      items: [this.productToItem(product, 1)],
    });
  }

  trackViewItemList(products: Product[], listName: string): void {
    if (!products.length) {
      return;
    }
    this.ecommerce('view_item_list', {
      item_list_name: listName,
      items: products.slice(0, 24).map((product, index) => this.productToItem(product, 1, index)),
    });
  }

  trackSelectItem(product: Product, listName?: string): void {
    this.ecommerce('select_item', {
      item_list_name: listName,
      items: [this.productToItem(product, 1)],
    });
  }

  trackAddToCart(product: Product, quantity: number): void {
    const price = getProductUnitPrice(product);
    const qty = Math.max(1, quantity);
    this.ecommerce('add_to_cart', {
      currency: GA4_CURRENCY,
      value: price * qty,
      items: [this.productToItem(product, qty)],
    });
  }

  trackRemoveFromCart(product: Product, quantity: number): void {
    const price = getProductUnitPrice(product);
    const qty = Math.max(1, quantity);
    this.ecommerce('remove_from_cart', {
      currency: GA4_CURRENCY,
      value: price * qty,
      items: [this.productToItem(product, qty)],
    });
  }

  trackViewCart(cart: Cart): void {
    if (!cart.cartItems?.length) {
      return;
    }
    this.ecommerce('view_cart', {
      currency: GA4_CURRENCY,
      value: this.cartValue(cart),
      items: this.cartToItems(cart),
    });
  }

  trackBeginCheckout(cart: Cart): void {
    if (!cart.cartItems?.length) {
      return;
    }
    this.ecommerce('begin_checkout', {
      currency: GA4_CURRENCY,
      value: this.cartValue(cart),
      items: this.cartToItems(cart),
    });
  }

  trackAddShippingInfo(cart: Cart, shippingTier?: string): void {
    if (!cart.cartItems?.length) {
      return;
    }
    this.ecommerce('add_shipping_info', {
      currency: GA4_CURRENCY,
      value: this.cartValue(cart),
      shipping_tier: shippingTier,
      items: this.cartToItems(cart),
    });
  }

  trackAddPaymentInfo(cart: Cart, paymentType?: string): void {
    if (!cart.cartItems?.length) {
      return;
    }
    this.ecommerce('add_payment_info', {
      currency: GA4_CURRENCY,
      value: this.cartValue(cart),
      payment_type: paymentType,
      items: this.cartToItems(cart),
    });
  }

  trackPurchase(orderId: string, totalAmount: number, cart: Cart): void {
    if (!this.markPurchaseTracked(orderId)) {
      return;
    }
    this.ecommerce('purchase', {
      transaction_id: orderId,
      currency: GA4_CURRENCY,
      value: totalAmount,
      items: this.cartToItems(cart),
    });
  }

  trackPurchaseFromOrder(order: StorefrontOrder): void {
    if (!this.markPurchaseTracked(order.orderNumber)) {
      return;
    }
    this.ecommerce('purchase', {
      transaction_id: order.orderNumber,
      currency: GA4_CURRENCY,
      value: order.totalAmount,
      items: (order.orderItems ?? []).map((item, i) => ({
        item_id: resolveMerchantId({
          id: item.productId,
          sku: item.product?.sku,
          merchantId: item.product?.merchantId,
        }),
        item_name: item.productName ?? item.product?.name ?? `Product #${item.productId}`,
        price: item.unitPrice,
        quantity: item.quantity,
        index: i,
        internal_product_id: item.productId,
      })),
    });
  }

  private ecommerce(event: string, ecommerce: Record<string, unknown>): void {
    if (!this.isEnabled) {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce });
  }

  private push(payload: Record<string, unknown>): void {
    if (!this.isEnabled) {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  private productToItem(
    product: Product,
    quantity: number,
    index?: number
  ): Record<string, unknown> {
    return {
      item_id: resolveMerchantId(product),
      item_name: product.name,
      item_category: product.categoryName ?? undefined,
      price: getProductUnitPrice(product),
      quantity,
      index,
      internal_product_id: product.id,
    };
  }

  private cartToItems(cart: Cart): Record<string, unknown>[] {
    return (cart.cartItems ?? []).map((item, i) => ({
      item_id: resolveMerchantId(item.product ?? { id: item.productId }),
      item_name: item.product?.name ?? `Product #${item.productId}`,
      price: item.unitPrice,
      quantity: item.quantity,
      index: i,
      internal_product_id: item.productId,
    }));
  }

  private cartValue(cart: Cart): number {
    return cart.cartItems?.reduce((s, i) => s + i.totalPrice, 0) ?? cart.totalAmount ?? 0;
  }

  private markPurchaseTracked(key: string): boolean {
    if (!key || typeof sessionStorage === 'undefined') {
      return true;
    }
    const k = `${this.purchaseDedupePrefix}${key}`;
    if (sessionStorage.getItem(k)) {
      return false;
    }
    sessionStorage.setItem(k, '1');
    return true;
  }
}
